import { Box } from '@chakra-ui/layout'
import {
    BoxProps,
    Button,
    IconButton,
    Skeleton,
    useMediaQuery
} from '@chakra-ui/react'
import { media } from 'components/utils/breakpoints'
import React, { useEffect, useState } from 'react'
import { Icon } from '../Icon'
import { usePages } from './hook'

import { PaginationProps } from './interfaces'

export const Pagination: React.FC<PaginationProps & BoxProps> = ({
    pages,
    currentPage,
    loading,
    changePage,
    ...props
}) => {
    const [isSmall, isMedium] = useMediaQuery([
        media('sm', 'max'),
        media('md', 'max')
    ])

    const {
        activePage: current,
        nextPage,
        prevPage,
        setPage,
        reset
    } = usePages({
        currentPage: currentPage ?? 1
    })

    const constr = (): (string | number)[] => {
        const difference = isMedium ? 2 : 2
        const minLength = isMedium ? 3 : 5
        const minimum = isMedium ? 5 : 7
        const initial =
            current - 1 <= difference
                ? Array.from({ length: minLength }, (_, i) => i + 1)
                : [1]
        const final =
            pages - current <= difference
                ? (Array.from({ length: minLength }).fill(pages) as number[])
                      .map((i, n) => i - n)
                      .sort((a, b) => a - b)
                : [pages]
        const middle = () => {
            const mid = current - 1 > difference && pages - current > difference
            if (isMedium && mid) {
                return ['...', current, '...']
            } else if (!isMedium && mid) {
                return ['...', current - 1, current, current + 1, '...']
            } else {
                return ['...']
            }
        }
        const result = isSmall
            ? [current]
            : pages < minimum
            ? Array.from({ length: pages }, (_, i) => i + 1)
            : [...initial, ...middle(), ...final]
        return result
    }

    const [items, setItems] = useState<(string | number)[]>(constr())

    const handlePage = (action: number | 'next' | 'prev') => {
        if (action === 'next' && current !== pages) {
            nextPage()
        } else if (action === 'prev' && current !== 1) {
            prevPage()
        } else {
            typeof action === 'number' && setPage(action)
        }
    }

    useEffect(() => {
        setItems(constr())
    }, [current, isMedium, isSmall, pages])

    useEffect(() => {
        changePage(current)
    }, [current])

    useEffect(() => {
        currentPage ? setPage(currentPage) : reset()
    }, [currentPage])

    return (
        <Box
            data-testid="pagination"
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
            gap={2}
            {...props}
        >
            <IconButton
                data-testid="pagination-prev"
                disabled={current <= 1}
                variant="ghost"
                colorScheme="primary"
                onClick={() => handlePage('prev')}
                aria-label="prev"
                icon={<Icon name="chevron-left" />}
            />
            {loading
                ? items.map((_, key: number) => (
                      <Skeleton
                          key={key}
                          height="32px"
                          width="40px"
                          borderRadius="2xl"
                      />
                  ))
                : items.map((page: number | string, key: number) => {
                      return (
                          <Button
                              data-testid={`pagination-page-${
                                  page === current
                                      ? 'selected'
                                      : page === '...'
                                      ? 'ellipsis'
                                      : page
                              }`}
                              key={key}
                              borderRadius="2xl"
                              minW="40px"
                              variant={page === current ? 'solid' : 'ghost'}
                              colorScheme="primary"
                              onClick={() =>
                                  typeof page === 'number' &&
                                  page !== current &&
                                  handlePage(page)
                              }
                          >
                              {page}
                          </Button>
                      )
                  })}
            <IconButton
                data-testid="pagination-next"
                disabled={current >= pages}
                variant="ghost"
                colorScheme="primary"
                onClick={() => handlePage('next')}
                aria-label="next"
                icon={<Icon name="chevron-right" />}
            />
        </Box>
    )
}
